import { LogOut, Settings, UserCog } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Button, Popover, PopoverContent, PopoverTrigger, Skeleton } from "@ceedero/ui";

import { AccountAvatar } from "@/account/components/account-avatar";
import { useAccountProfile, useSignOut } from "@/account/hooks/use-account";
import { useTranslation } from "@/localization/hooks/use-translation";
import { ROUTES } from "@/navigation/constants/routes";

/**
 * Who is signed in, and the two things they can do about it.
 *
 * The avatar rather than the username in the bar, and the account behind it
 * rather than beside the pages: the account is not a place in this application
 * the way the catalog and the devices are — it is whose application this is.
 * The desktop draws the same control in the same corner, so a user moving
 * between them reaches for the same thing.
 */
export const AccountMenu = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const profile = useAccountProfile();
  const signOut = useSignOut();

  const isInitialLoading = profile.isPending && !profile.data;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={className}
          aria-label={t("account.open")}
          aria-busy={isInitialLoading || undefined}
        >
          <AccountAvatar
            profile={profile.data}
            isLoading={isInitialLoading}
            className="size-7 rounded-full"
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        role="dialog"
        aria-labelledby="account-menu-title"
        className="w-[min(20rem,calc(100vw-1rem))] p-3"
      >
        <div className="rounded-lg bg-muted/60 px-4 py-5 text-center">
          <h2 id="account-menu-title" className="sr-only">
            {t("account.title")}
          </h2>
          <AccountAvatar
            profile={profile.data}
            isLoading={isInitialLoading}
            className="mx-auto size-16 rounded-full"
          />

          {isInitialLoading ? (
            <div className="mt-4 flex flex-col items-center gap-2">
              <Skeleton className="h-5 w-36" />
              <Skeleton className="h-3 w-24" />
            </div>
          ) : (
            <div className="mt-4">
              {/*
                The display name is optional, so the username is what is always
                there — and it is the name they sign in with, which makes it the
                more useful of the two to confirm.
              */}
              <p className="font-semibold">{profile.data?.displayName || profile.data?.username}</p>
              {profile.data?.displayName && (
                <p className="mt-1 text-xs text-muted-foreground">{profile.data.username}</p>
              )}
            </div>
          )}

          {profile.data?.licenseDisplayKey && (
            <p className="mt-3 font-mono text-xs text-muted-foreground">
              {profile.data.licenseDisplayKey}
            </p>
          )}

          <div className="mt-5 flex flex-col gap-2">
            <Button asChild type="button" variant="outline" className="w-full">
              <Link to={ROUTES.account} onClick={() => setIsOpen(false)}>
                <UserCog />
                {t("account.manage")}
              </Link>
            </Button>
            <Button asChild type="button" variant="ghost" className="w-full">
              <Link to={ROUTES.settings} onClick={() => setIsOpen(false)}>
                <Settings />
                {t("settings.title")}
              </Link>
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => signOut.mutate()}
              isLoading={signOut.isPending}
            >
              <LogOut />
              {t("navigation.signOut")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
